import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { KeyRound, Loader2, ShieldCheck, XCircle } from "lucide-react";
import { toast } from "sonner";
import driverApi from "@/lib/driverApi";

export default function DriverSetup() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const [state, setState] = useState("verifying"); // verifying | ready | invalid
  const [error, setError] = useState("");
  const [me, setMe] = useState(null);
  const [pin, setPin] = useState("");
  const [pin2, setPin2] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!token) {
      setError("Nedostaje aktivacijska poveznica.");
      setState("invalid");
      return;
    }
    driverApi.post("/driver/setup/verify", { token })
      .then(({ data }) => { setMe(data); setState("ready"); })
      .catch((err) => {
        setError(err?.response?.data?.detail || "Poveznica nije važeća.");
        setState("invalid");
      });
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    if (!/^\d{4,8}$/.test(pin)) {
      toast.error("PIN mora imati 4–8 znamenki.");
      return;
    }
    if (pin !== pin2) {
      toast.error("PIN-ovi se ne podudaraju.");
      return;
    }
    setBusy(true);
    try {
      const { data } = await driverApi.post("/driver/setup/complete", { token, pin });
      localStorage.setItem("juke_driver_token", data.access_token);
      toast.success("Račun aktiviran — dobrodošli!");
      navigate("/driver/dashboard");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Aktivacija nije uspjela.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid="driver-setup-page" className="min-h-screen bg-[#050505] text-zinc-100 flex flex-col">
      <div className="pt-14 pb-6 text-center">
        <div className="inline-flex items-baseline gap-2">
          <span className="font-display text-3xl text-white">Juke</span>
          <span className="font-display text-3xl text-[#C5A059] italic">Transfers</span>
        </div>
        <p className="mt-2 text-[11px] uppercase tracking-[0.3em] text-zinc-500">Aktivacija vozačkog računa</p>
      </div>

      <div className="flex-1 px-6 max-w-md w-full mx-auto flex flex-col justify-center">
        {state === "verifying" && (
          <div className="flex items-center justify-center gap-2 text-zinc-500 text-sm">
            <Loader2 className="w-4 h-4 animate-spin" /> Provjera poveznice…
          </div>
        )}

        {state === "invalid" && (
          <div data-testid="setup-invalid" className="rounded-2xl border border-red-500/30 bg-red-500/5 p-6 text-center">
            <XCircle className="w-8 h-8 text-red-400 mx-auto mb-3" />
            <p className="text-sm text-zinc-200">{error}</p>
            <p className="text-xs text-zinc-500 mt-3">
              Javite se Juke Transfersu i poslat ćemo vam novu aktivacijsku poveznicu.
            </p>
          </div>
        )}

        {state === "ready" && (
          <form onSubmit={submit} className="space-y-5">
            <div className="rounded-2xl border border-[#C5A059]/30 bg-[#C5A059]/5 p-4">
              <p className="text-[10px] uppercase tracking-[0.25em] text-[#C5A059]">Dobrodošli u flotu</p>
              <p className="text-base text-white mt-1" data-testid="setup-driver-name">{me?.name}</p>
              <p className="text-xs text-zinc-500 mt-0.5">{me?.phone}</p>
            </div>
            <p className="text-sm text-zinc-400 leading-relaxed">
              Postavite vlastiti PIN (4–8 znamenki) za prijavu u vozačku aplikaciju.
            </p>
            <div>
              <label className="text-xs uppercase tracking-[0.2em] text-zinc-400 mb-2 block">Novi PIN</label>
              <div className="relative">
                <KeyRound className="w-5 h-5 text-zinc-500 absolute left-4 top-1/2 -translate-y-1/2" />
                <input
                  data-testid="setup-pin-input"
                  type="password" inputMode="numeric" autoComplete="new-password"
                  maxLength={8} value={pin}
                  onChange={(e) => setPin(e.target.value.replace(/\D/g, ""))}
                  className="w-full h-14 pl-12 pr-4 rounded-2xl bg-[#0d0d0d] border border-white/10 focus:border-[#C5A059] outline-none text-lg tracking-[0.3em]"
                  placeholder="••••"
                />
              </div>
            </div>
            <div>
              <label className="text-xs uppercase tracking-[0.2em] text-zinc-400 mb-2 block">Potvrdite PIN</label>
              <div className="relative">
                <KeyRound className="w-5 h-5 text-zinc-500 absolute left-4 top-1/2 -translate-y-1/2" />
                <input
                  data-testid="setup-pin-confirm-input"
                  type="password" inputMode="numeric" autoComplete="new-password"
                  maxLength={8} value={pin2}
                  onChange={(e) => setPin2(e.target.value.replace(/\D/g, ""))}
                  className="w-full h-14 pl-12 pr-4 rounded-2xl bg-[#0d0d0d] border border-white/10 focus:border-[#C5A059] outline-none text-lg tracking-[0.3em]"
                  placeholder="••••"
                />
              </div>
            </div>
            <button
              type="submit" disabled={busy}
              data-testid="setup-submit-btn"
              className="w-full h-14 rounded-2xl bg-[#C5A059] hover:bg-[#d4b06a] disabled:opacity-60 text-black font-semibold text-sm uppercase tracking-[0.2em] flex items-center justify-center gap-2 transition-colors"
            >
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <ShieldCheck className="w-4 h-4" />}
              Aktiviraj i prijavi se
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
